import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { AdminBreadcrumbs } from '@routes/admin-dashboard/AdminBreadcrumbs';
import { QUESTION } from '@constants/terminology';
import { EditorView, QuestionManagement, ShowAs } from '@features/question-management/QuestionManagement';
import { useAppSelector } from '@reducers/index';
import { isRootOrg } from '@selectors/initiative';
import { BasicAlert } from '@g17eco/molecules/alert';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';

const EDITOR_HASH = '#editor';

export const AdminQuestionRoute = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const isRootOrganization = useAppSelector(isRootOrg);
  const history = useHistory();

  const basePath = generateUrl(ROUTES.ADMIN_DASHBOARD, { initiativeId });

  const [editorView, setEditorView] = useState<EditorView | null>(null);

  // Listen for browser back/forward to sync editor state
  useEffect(() => {
    return history.listen((newLocation, action) => {
      if (action === 'POP' && !newLocation.hash?.includes(EDITOR_HASH)) {
        setEditorView(null);
      }
    });
  }, [history]);

  const handleEditorViewChange = (view: EditorView | null) => {
    setEditorView(view);
    if (view) {
      history.push({ ...history.location, hash: EDITOR_HASH });
    } else if (history.location.hash?.includes(EDITOR_HASH)) {
      history.goBack();
    }
  };

  return (
    <div className='admin-dashboard'>
      <Dashboard>
        <DashboardRow>
          <AdminBreadcrumbs
            initiativeId={initiativeId}
            breadcrumbs={
              editorView
                ? [
                    {
                      label: `${QUESTION.CAPITALIZED_SINGULAR} management`,
                      url: basePath + '/questions',
                    },
                    {
                      label: `${QUESTION.CAPITALIZED_SINGULAR} Editor`,
                    },
                  ]
                : [
                    {
                      label: `${QUESTION.CAPITALIZED_SINGULAR} management`,
                    },
                  ]
            }
          />
        </DashboardRow>
        <DashboardSectionTitle title={editorView ? `${QUESTION.CAPITALIZED_SINGULAR} Editor` : `${QUESTION.CAPITALIZED_SINGULAR} management`} />
        <DashboardSection>
          {isRootOrganization ? null : (
            <BasicAlert type='warning'>
              Overrides cannot be modified on this level. Overrides must be set on the root level or the organisation
            </BasicAlert>
          )}
          <QuestionManagement
            showAs={ShowAs.Page}
            initiativeId={initiativeId}
            pageSize={50}
            editorView={editorView}
            onEditorViewChange={handleEditorViewChange}
            onNavigateToValueList={(utrId) =>
              history.push(`${basePath}/questions/${utrId}/dropdown-options`)
            }
          />
        </DashboardSection>
      </Dashboard>
    </div>
  );
};
